'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useTrack } from '@owlcat/app-shell';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { useAudioRecorder } from '@/contexts/media/hooks/useAudioRecorder';
import { MicLevelMeter } from '@/contexts/media/components/MicLevelMeter';
import { MIC_QUIET_LEVEL, MIC_CLIP_LEVEL } from '@/contexts/media/audio/levelMeter';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { Mic, Square, Play, Pause, RotateCcw, Check, AlertCircle, Loader2, Lightbulb } from 'lucide-react';
import type { CustomVoiceEntry, VoiceGender } from '../types/voice';
import { cloneVoice } from '@/contexts/ai/actions/cloneVoice';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';

/**
 * Wizard step identifiers
 */
type WizardStep = 'intro' | 'recording' | 'details' | 'cloning';

/**
 * The single recorded take
 */
interface RecordedTake {
  blob: Blob;
  url: string;
  duration: number;
  /** Script language shown while this take was recorded (→ scriptText). */
  scriptLanguage: ScriptLanguage;
}

/**
 * Props for VoiceCloneWizard component
 */
export interface VoiceCloneWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyId: string;
  /** Called when clone job is enqueued (voice is pending). Use voiceEntryId to match SSE events. */
  onEnqueued?: (voiceEntryId: string) => void;
  /** Called when clone completes (SSE confirmed). Deprecated — use onEnqueued + SSE in parent instead. */
  onSuccess?: (voice: CustomVoiceEntry) => void;
}

/** Minimum take length — "Готово" is disabled below this. */
export const MIN_RECORDING_SECONDS = 30;
/** Target band shown to the user (best clones: ~45–75 s of ONE register). */
export const TARGET_MIN_SECONDS = 45;
export const TARGET_MAX_SECONDS = 75;
/** Hard cap — the recorder auto-stops here. */
export const MAX_RECORDING_SECONDS = 90;

/** Sustained-low window before "too quiet" (ms). */
const QUIET_HINT_MS = 3000;
/** Accumulated clipping before "too loud" (ms). */
const LOUD_HINT_MS = 1000;
/** Hint evaluation cadence (ms). */
const HINT_TICK_MS = 250;
/** After this long in "processing" the stage text flips to "creating the voice". */
const CREATING_STAGE_AFTER_MS = 8000;
/** Reference-quality read-back: the consumer persists after the SSE — retry a few times. */
const QUALITY_POLL_MS = 1500;
const QUALITY_POLL_MAX = 6;

/**
 * Maximum voice name length
 */
const MAX_NAME_LENGTH = 50;

/**
 * Supported script languages
 */
export type ScriptLanguage = 'en' | 'ru' | 'cs' | 'uk' | 'de' | 'fr';

const SCRIPT_LANGUAGES: ScriptLanguage[] = ['ru', 'en', 'uk', 'cs', 'de', 'fr'];

/**
 * Script language flag emojis
 */
const SCRIPT_LANGUAGE_FLAGS: Record<ScriptLanguage, string> = {
  en: '\u{1F1EC}\u{1F1E7}',
  ru: '\u{1F1F7}\u{1F1FA}',
  cs: '\u{1F1E8}\u{1F1FF}',
  uk: '\u{1F1FA}\u{1F1E6}',
  de: '\u{1F1E9}\u{1F1EA}',
  fr: '\u{1F1EB}\u{1F1F7}',
};

function toScriptLanguage(locale: string | undefined): ScriptLanguage {
  const short = (locale ?? '').slice(0, 2).toLowerCase();
  return (SCRIPT_LANGUAGES as string[]).includes(short) ? (short as ScriptLanguage) : 'ru';
}

/**
 * Formats seconds into MM:SS display string
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

type LiveHint = 'tooQuiet' | 'tooLoud' | null;
type CloningStage = 'processing' | 'creating';

/** Reference-quality issue codes the worker reports that have a user-facing hint. */
const QUALITY_ISSUE_HINTS = ['noisy', 'too_quiet', 'clipped', 'too_short'] as const;
type QualityIssueHint = (typeof QUALITY_ISSUE_HINTS)[number];
const LOW_ADHERENCE = 0.6;

/** Soft, non-blocking hints derived from the persisted reference-quality report. */
export function qualityHintsFor(entry: Pick<CustomVoiceEntry, 'referenceQuality' | 'scriptAdherence'>): Array<QualityIssueHint | 'lowAdherence'> {
  const hints: Array<QualityIssueHint | 'lowAdherence'> = [];
  for (const issue of entry.referenceQuality?.issues ?? []) {
    if ((QUALITY_ISSUE_HINTS as readonly string[]).includes(issue)) hints.push(issue as QualityIssueHint);
  }
  if (typeof entry.scriptAdherence === 'number' && entry.scriptAdherence < LOW_ADHERENCE) hints.push('lowAdherence');
  return hints;
}

/**
 * VoiceCloneWizard — a 4-step dialog for cloning a voice from ONE take.
 *
 * Steps:
 * 1. Introduction — recording tips (quiet room, distance, warm storytelling register)
 * 2. Recording — ONE take of a short two-paragraph script (30 s min, 45–75 s target, 90 s cap)
 *    with live level hints (too quiet / too loud), then playback + re-record
 * 3. Details — name, gender, language
 * 4. Cloning — stage progress, then preview + soft quality hints when the SSE reports ready
 */
export function VoiceCloneWizard({
  open,
  onOpenChange,
  familyId,
  onEnqueued,
  onSuccess,
}: VoiceCloneWizardProps) {
  const t = useTranslations('cloneVoice');
  const track = useTrack();
  const locale = useLocale();
  const defaultLanguage = toScriptLanguage(locale);

  // Wizard state
  const [step, setStep] = useState<WizardStep>('intro');
  const [take, setTake] = useState<RecordedTake | null>(null);
  const [voiceName, setVoiceName] = useState('');
  const [gender, setGender] = useState<VoiceGender | ''>('');
  // Explicit rights/consent confirmation (ToS §10): own voice or a family member's with consent.
  const [consented, setConsented] = useState(false);
  const [language, setLanguage] = useState<ScriptLanguage>(defaultLanguage);
  const [liveHint, setLiveHint] = useState<LiveHint>(null);

  // Cloning state
  const [isCloning, setIsCloning] = useState(false);
  const [cloningStage, setCloningStage] = useState<CloningStage>('processing');
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [clonedVoice, setClonedVoice] = useState<CustomVoiceEntry | null>(null);
  const [qualityHints, setQualityHints] = useState<Array<QualityIssueHint | 'lowAdherence'>>([]);

  // Pending voice entry ID — used to match SSE events
  const pendingVoiceEntryIdRef = useRef<string | null>(null);
  const cloneEngineRef = useRef<string>('unknown');
  const qualityPollCancelRef = useRef<(() => void) | null>(null);

  // Audio preview ('take' | 'preview' | null)
  const [playing, setPlaying] = useState<'take' | 'preview' | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Audio recorder hook — hard cap = auto-stop.
  const recorder = useAudioRecorder({ maxDurationSeconds: MAX_RECORDING_SECONDS });
  const levelRef = useRef(0);
  levelRef.current = recorder.level;
  const discardNextTakeRef = useRef(false);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlaying(null);
  }, []);

  // Auto-start recording when entering the recording step
  const prevStepRef = useRef<WizardStep>('intro');
  useEffect(() => {
    if (step === 'recording' && prevStepRef.current === 'intro') {
      recorder.startRecording();
    }
    prevStepRef.current = step;
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Live level hints while recording: sustained-low → "too quiet", accumulated
   * clipping → "too loud". Evaluated on a fixed tick from the latest level.
   */
  useEffect(() => {
    if (!recorder.isRecording) {
      setLiveHint(null);
      return;
    }
    let quietSince = Date.now();
    let loudAccumMs = 0;
    let last = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      const dt = now - last;
      last = now;
      const level = levelRef.current;
      if (level >= MIC_QUIET_LEVEL) quietSince = now;
      loudAccumMs = level >= MIC_CLIP_LEVEL ? loudAccumMs + dt : Math.max(0, loudAccumMs - dt / 2);
      if (loudAccumMs >= LOUD_HINT_MS) setLiveHint('tooLoud');
      else if (now - quietSince >= QUIET_HINT_MS) setLiveHint('tooQuiet');
      else setLiveHint(null);
    }, HINT_TICK_MS);
    return () => clearInterval(id);
  }, [recorder.isRecording]);

  /**
   * Reset wizard to initial state
   */
  const resetWizard = useCallback(() => {
    setStep('intro');
    setTake((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
    setVoiceName('');
    setGender('');
    setConsented(false);
    setLanguage(defaultLanguage);
    setLiveHint(null);
    setIsCloning(false);
    setCloningStage('processing');
    setCloneError(null);
    setClonedVoice(null);
    setQualityHints([]);
    stopPlayback();
    pendingVoiceEntryIdRef.current = null;
    discardNextTakeRef.current = false;
    recorder.resetRecording();
  }, [recorder, defaultLanguage, stopPlayback]);

  /**
   * Handle dialog open state change.
   * If recording is active, prevent accidental close.
   */
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && recorder.isRecording) {
        // Prevent closing while recording — user must stop first
        return;
      }
      if (!nextOpen) {
        resetWizard();
      }
      onOpenChange(nextOpen);
    },
    [recorder.isRecording, resetWizard, onOpenChange]
  );

  /**
   * Finish the take (≥ MIN_RECORDING_SECONDS)
   */
  const handleFinishTake = useCallback(() => {
    recorder.stopRecording();
  }, [recorder]);

  /**
   * Abort the current take — stop and drop whatever was captured.
   */
  const handleAbortTake = useCallback(() => {
    discardNextTakeRef.current = true;
    recorder.stopRecording();
  }, [recorder]);

  /**
   * When recording stops and we have a blob, keep it as THE take
   */
  useEffect(() => {
    if (!recorder.isRecording && recorder.audioBlob && recorder.audioUrl && step === 'recording') {
      if (discardNextTakeRef.current) {
        discardNextTakeRef.current = false;
        recorder.resetRecording();
        return;
      }
      const newTake: RecordedTake = {
        blob: recorder.audioBlob,
        // Create a NEW object URL so resetRecording() doesn't revoke it
        url: URL.createObjectURL(recorder.audioBlob),
        duration: recorder.duration,
        scriptLanguage: language,
      };
      setTake((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return newTake;
      });
      recorder.resetRecording();
    }
  }, [recorder.isRecording, recorder.audioBlob, recorder.audioUrl, recorder.duration, step, recorder, language]);

  /**
   * Drop the take and start over
   */
  const handleRerecord = useCallback(() => {
    stopPlayback();
    setTake((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
    recorder.startRecording();
  }, [recorder, stopPlayback]);

  /**
   * Play/pause an audio url
   */
  const togglePlay = useCallback(
    (which: 'take' | 'preview', url: string) => {
      if (playing === which) {
        stopPlayback();
        return;
      }
      stopPlayback();
      const audio = new Audio(mediaSrc(url));
      audio.onended = () => setPlaying(null);
      audioRef.current = audio;
      audio.play();
      setPlaying(which);
    },
    [playing, stopPlayback]
  );

  /**
   * Submit voice clone request
   */
  const handleClone = useCallback(async () => {
    if (!voiceName.trim() || !take) return;

    setStep('cloning');
    setIsCloning(true);
    setCloningStage('processing');
    setCloneError(null);
    setClonedVoice(null);
    setQualityHints([]);

    try {
      const voiceEntryId = crypto.randomUUID();

      // Upload the take via FormData (avoids RSC serialization limits)
      const formData = new FormData();
      formData.set('file', take.blob, 'sample_0.webm');
      formData.set('voiceEntryId', voiceEntryId);
      formData.set('sampleIndex', '0');

      // A route, not a server action: the take is around a megabyte and Next's
      // action body parser rejected it before any of our code ran, leaving the
      // user with "an unexpected response was received from the server" and the
      // server with nothing in its log.
      const res = await fetch('/api/voice-samples', { method: 'POST', body: formData });
      const uploaded = await res.json().catch(() => null);
      if (!res.ok || !uploaded?.url) {
        throw new Error(uploaded?.error || `Upload failed (HTTP ${res.status})`);
      }

      // Store pending ID BEFORE enqueue — the SSE may beat the action's return
      pendingVoiceEntryIdRef.current = voiceEntryId;

      // Call server action with the pre-uploaded URL (no base64 in payload)
      const result = await cloneVoice({
        name: voiceName.trim(),
        sampleUrls: [uploaded.url],
        voiceEntryId,
        familyId,
        language,
        gender: gender || undefined,
        // The exact script the user read (adherence check on the transcript)
        scriptText: t(`script.${take.scriptLanguage}`),
      });

      if (!result.success) {
        pendingVoiceEntryIdRef.current = null;
        throw new Error(result.error || 'Clone failed');
      }

      cloneEngineRef.current = result.data.provider ?? 'unknown';
      onEnqueued?.(result.data.voiceEntryId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to clone voice';
      setCloneError(message);
      setIsCloning(false);
    }
  }, [voiceName, take, familyId, gender, language, t, onEnqueued]);

  /** Stage text: "processing…" → "creating the voice…" after a while. */
  useEffect(() => {
    if (!isCloning) return;
    const id = setTimeout(() => setCloningStage('creating'), CREATING_STAGE_AFTER_MS);
    return () => clearTimeout(id);
  }, [isCloning]);

  /**
   * SSE: our clone is ready → show preview, then read back the quality report
   * (persisted by the consumer, possibly a moment after the SSE).
   */
  useEventSourceEvent('voice.clone.completed', useCallback((payload: Record<string, unknown>) => {
    const voiceEntryId = payload.voiceEntryId as string | undefined;
    if (!voiceEntryId || voiceEntryId !== pendingVoiceEntryIdRef.current) return;
    pendingVoiceEntryIdRef.current = null;

    const ready: CustomVoiceEntry = {
      id: voiceEntryId,
      voiceId: (payload.voiceId as string) || voiceEntryId,
      name: (payload.name as string) || voiceName,
      source: 'cloned',
      previewUrl: payload.previewUrl as string | undefined,
      addedBy: familyId,
      addedAt: new Date().toISOString(),
      status: 'ready',
      language,
    };
    setClonedVoice(ready);
    setIsCloning(false);
    // Fleet analytics: the engine the clone was enqueued on (from the action result).
    track('voice_clone_created', { engine: cloneEngineRef.current });
    onSuccess?.(ready);

    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      attempts += 1;
      try {
        const { getCustomVoiceEntry } = await import('@/contexts/ai/actions/getCustomVoiceEntry');
        const res = await getCustomVoiceEntry(voiceEntryId);
        if (cancelled) return;
        if (res.success && res.data && (res.data.referenceQuality || typeof res.data.scriptAdherence === 'number')) {
          setQualityHints(qualityHintsFor(res.data));
          return;
        }
      } catch {
        // best-effort — the hint is a nicety
      }
      if (!cancelled && attempts < QUALITY_POLL_MAX) setTimeout(poll, QUALITY_POLL_MS);
    };
    void poll();
    qualityPollCancelRef.current = () => { cancelled = true; };
  }, [familyId, language, voiceName, onSuccess, track]));

  /** SSE: the clone failed (payload has no entry id — only while ours is pending). */
  useEventSourceEvent('voice.clone.failed', useCallback((payload: Record<string, unknown>) => {
    if (!pendingVoiceEntryIdRef.current) return;
    pendingVoiceEntryIdRef.current = null;
    setCloneError((payload.error as string) || t('cloneFailed'));
    setIsCloning(false);
  }, [t]));

  /**
   * Retry cloning after error
   */
  const handleRetry = useCallback(() => {
    setCloneError(null);
    handleClone();
  }, [handleClone]);

  /**
   * Voice name validation
   */
  const nameError = voiceName.length > 0 && voiceName.trim().length < 1
    ? t('nameRequired')
    : voiceName.length > MAX_NAME_LENGTH
      ? t('nameTooLong')
      : null;

  const canProceedToClone =
    voiceName.trim().length >= 1 &&
    voiceName.length <= MAX_NAME_LENGTH &&
    !!take &&
    consented;

  // Cleanup audio element + quality poll on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      qualityPollCancelRef.current?.();
    };
  }, []);

  const canFinish = recorder.duration >= MIN_RECORDING_SECONDS;
  const inTargetBand = recorder.duration >= TARGET_MIN_SECONDS && recorder.duration <= TARGET_MAX_SECONDS;
  const autoStopped = !!take && take.duration >= MAX_RECORDING_SECONDS;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90dvh] flex flex-col max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:rounded-none max-sm:border-0" showCloseButton={!recorder.isRecording}>
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>
            {step === 'intro' && t('introDescription')}
            {step === 'recording' && t('recordingDescription')}
            {step === 'details' && t('detailsDescription')}
            {step === 'cloning' && t('cloningDescription')}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Introduction */}
        {step === 'intro' && (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
              <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-warning" />
                  {t('tips.title')}
                </h3>
                <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside" data-testid="clone-tips">
                  <li>{t('tips.quietRoom')}</li>
                  <li>{t('tips.noMusic')}</li>
                  <li>{t('tips.distance')}</li>
                  <li>{t('tips.warmRead')}</li>
                  <li>{t('tips.dontRush')}</li>
                  <li>{t('tips.stumble')}</li>
                </ul>
                <p className="text-sm">{t('takeLength', { min: TARGET_MIN_SECONDS, max: TARGET_MAX_SECONDS })}</p>
              </div>

              {/* Script language selector — drives the text shown while recording */}
              <div className="space-y-2">
                <Label>{t('languageLabel')}</Label>
                <Select
                  value={language}
                  onValueChange={(v) => setLanguage(v as ScriptLanguage)}
                >
                  <SelectTrigger data-testid="script-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCRIPT_LANGUAGES.map((lang) => (
                      <SelectItem key={lang} value={lang}>
                        {SCRIPT_LANGUAGE_FLAGS[lang]} {t(`languages.${lang}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('languageHint')}</p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t('cancel')}
              </Button>
              <Button onClick={() => setStep('recording')}>
                <Mic className="h-4 w-4" />
                {t('startRecording')}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 2: Recording — ONE take */}
        {step === 'recording' && (
          <>
            <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto">
              {/* Status strip: FIXED geometry so the script never moves — every
                  element keeps its slot (invisible placeholders instead of
                  mount/unmount), the meter has a fixed height, the hint row a
                  reserved min-height. */}
              {recorder.isRecording && (
                <div className="sticky top-0 z-10 -mx-1 space-y-2 bg-background px-1 pb-2" data-testid="recording-status">
                  <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 rounded-full bg-destructive animate-pulse" />
                      <span className="font-mono text-lg tabular-nums w-[5ch]" data-testid="recording-duration">
                        {formatDuration(recorder.duration)}
                      </span>
                    </div>
                    <MicLevelMeter level={recorder.level} elapsedSeconds={recorder.duration} className="min-w-0" />
                    <span
                      className={`text-xs tabular-nums whitespace-nowrap ${inTargetBand ? 'text-success' : 'text-muted-foreground'}`}
                      data-testid="target-band"
                    >
                      {t('targetBand', { min: TARGET_MIN_SECONDS, max: TARGET_MAX_SECONDS })}
                    </span>
                  </div>
                  <div
                    className={`min-h-[2.25rem] rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      liveHint
                        ? 'border-warning/40 bg-warning/10 text-warning-foreground dark:text-warning'
                        : 'border-transparent text-muted-foreground'
                    }`}
                    role="status"
                    data-testid={liveHint ? `live-hint-${liveHint}` : 'live-hint-none'}
                  >
                    {liveHint
                      ? t(`hint.${liveHint}`)
                      : !canFinish
                        ? t('minToFinish', { seconds: MIN_RECORDING_SECONDS })
                        : '\u00a0'}
                  </div>
                </div>
              )}

              {/* Script — the text to read, two short paragraphs */}
              {!take && (
                <div
                  className="rounded-lg border p-4 text-lg leading-relaxed sm:text-xl space-y-4"
                  data-testid="dictation-text"
                >
                  {t(`script.${language}`).split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              )}

              {/* The recorded take — playback */}
              {take && !recorder.isRecording && (
                <div className="space-y-3" data-testid="recorded-take">
                  <div className="rounded-lg bg-success/10 border border-success/30 p-3 text-sm text-success">
                    {t('takeReady', { duration: formatDuration(take.duration) })}
                  </div>
                  {autoStopped && (
                    <p className="text-xs text-muted-foreground">
                      {t('autoStopped', { seconds: MAX_RECORDING_SECONDS })}
                    </p>
                  )}
                  <div className="flex items-center gap-2 rounded-md border p-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => togglePlay('take', take.url)}
                      aria-label={playing === 'take' ? t('pause') : t('play')}
                    >
                      {playing === 'take' ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <span className="text-sm flex-1">
                      {t('take')} — {formatDuration(take.duration)}
                    </span>
                  </div>
                </div>
              )}

              {/* Recorder error with human-readable messages */}
              {recorder.error && (
                <div className="space-y-2" role="alert">
                  <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {recorder.error === 'microphone_permission_denied'
                        ? t('error.permissionDenied')
                        : recorder.error === 'microphone_not_found'
                          ? t('error.microphoneNotFound')
                          : recorder.error === 'microphone_not_available'
                            ? t('error.microphoneNotAvailable')
                            : recorder.error}
                    </span>
                  </div>
                  {recorder.error === 'microphone_permission_denied' && (
                    <p className="text-xs text-muted-foreground pl-6">
                      {t('error.permissionInstruction')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Recording actions */}
            <DialogFooter className="flex-row gap-2">
              {recorder.isRecording ? (
                <>
                  <Button variant="outline" onClick={handleAbortTake}>
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleFinishTake}
                    disabled={!canFinish}
                    data-testid="finish-take"
                  >
                    <Square className="h-4 w-4" />
                    {t('finishTake')}
                  </Button>
                </>
              ) : take ? (
                <>
                  <Button variant="outline" onClick={handleRerecord} data-testid="rerecord">
                    <RotateCcw className="h-4 w-4" />
                    {t('rerecord')}
                  </Button>
                  <Button onClick={() => { stopPlayback(); setStep('details'); }} data-testid="recording-next">
                    {t('next')}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => handleOpenChange(false)}>
                    {t('cancel')}
                  </Button>
                  <Button onClick={() => recorder.startRecording()}>
                    <Mic className="h-4 w-4" />
                    {t('startRecording')}
                  </Button>
                </>
              )}
            </DialogFooter>
          </>
        )}

        {/* Step 3: Details */}
        {step === 'details' && (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
              {/* Voice name */}
              <div className="space-y-2">
                <Label htmlFor="voice-name">{t('nameLabel')}</Label>
                <Input
                  id="voice-name"
                  value={voiceName}
                  onChange={(e) => setVoiceName(e.target.value)}
                  placeholder={t('namePlaceholder')}
                  maxLength={MAX_NAME_LENGTH}
                  aria-invalid={!!nameError}
                />
                {nameError && (
                  <p className="text-sm text-destructive">{nameError}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {voiceName.length}/{MAX_NAME_LENGTH}
                </p>
              </div>

              {/* Gender */}
              <div className="space-y-2">
                <Label>{t('genderLabel')}</Label>
                <div className="flex gap-2">
                  <Button
                    variant={gender === 'male' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGender(gender === 'male' ? '' : 'male')}
                    data-testid="gender-male"
                  >
                    {t('genderMale')}
                  </Button>
                  <Button
                    variant={gender === 'female' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setGender(gender === 'female' ? '' : 'female')}
                    data-testid="gender-female"
                  >
                    {t('genderFemale')}
                  </Button>
                </div>
              </div>

              {/* Language */}
              <div className="space-y-2">
                <Label>{t('languageLabel')}</Label>
                <Select
                  value={language}
                  onValueChange={(v) => setLanguage(v as ScriptLanguage)}
                >
                  <SelectTrigger data-testid="voice-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCRIPT_LANGUAGES.map((lang) => (
                      <SelectItem key={lang} value={lang}>
                        {SCRIPT_LANGUAGE_FLAGS[lang]} {t(`languages.${lang}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('languageHint')}</p>
              </div>

              {/* Take preview */}
              {take && (
                <div className="space-y-2">
                  <Label>{t('take')}</Label>
                  <div className="flex items-center gap-2 rounded-md border p-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => togglePlay('take', take.url)}
                      aria-label={playing === 'take' ? t('pause') : t('play')}
                    >
                      {playing === 'take' ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <span className="text-sm flex-1">
                      {t('take')} — {formatDuration(take.duration)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm" data-testid="clone-consent">
              <Checkbox checked={consented} onCheckedChange={(v) => setConsented(v === true)} className="mt-0.5" />
              <span>
                {t('consentLabel')}
                <span className="block text-xs text-muted-foreground">{t('consentHint')}</span>
              </span>
            </label>

            <DialogFooter>
              <Button variant="outline" onClick={() => { stopPlayback(); setStep('recording'); }}>
                {t('back')}
              </Button>
              <Button onClick={handleClone} disabled={!canProceedToClone} data-testid="clone-submit">
                {t('save')}
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Step 4: Cloning */}
        {step === 'cloning' && (
          <div className="space-y-4 py-4">
            {isCloning && (
              <div className="flex flex-col items-center gap-4" data-testid="cloning-progress">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-lg font-medium" data-testid="cloning-stage">{t(`stage.${cloningStage}`)}</p>
                <Progress value={cloningStage === 'processing' ? 30 : 70} className="w-full" />
              </div>
            )}

            {cloneError && (
              <div className="flex flex-col items-center gap-4" data-testid="clone-error">
                <AlertCircle className="h-10 w-10 text-destructive" />
                <p className="text-lg font-medium text-destructive">{t('cloneFailed')}</p>
                <p className="text-sm text-muted-foreground text-center">{cloneError}</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep('details')}>
                    {t('back')}
                  </Button>
                  <Button onClick={handleRetry}>
                    <RotateCcw className="h-4 w-4" />
                    {t('retry')}
                  </Button>
                </div>
              </div>
            )}

            {clonedVoice && (
              <div className="flex flex-col items-center gap-4" data-testid="clone-success">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/20">
                  <Check className="h-6 w-6 text-success" />
                </div>
                <p className="text-lg font-medium">{t('success')}</p>
                <p className="text-sm text-muted-foreground">
                  {clonedVoice.name}
                </p>
                {/* TTS-generated voice preview */}
                {clonedVoice.previewUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => togglePlay('preview', clonedVoice.previewUrl!)}
                    data-testid="clone-preview"
                  >
                    {playing === 'preview' ? (
                      <Square className="h-4 w-4" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    {playing === 'preview' ? t('stopPreview') : t('playPreview')}
                  </Button>
                )}
                {/* Soft, non-blocking quality hints from the reference report */}
                {qualityHints.length > 0 && (
                  <div
                    className="w-full rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning-foreground dark:text-warning space-y-1"
                    data-testid="quality-hints"
                  >
                    {qualityHints.map((h) => (
                      <p key={h}>{t(`qualityHint.${h}`)}</p>
                    ))}
                  </div>
                )}
                <Button onClick={() => handleOpenChange(false)}>
                  {t('done')}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
